import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { Project } from "types";
import { TrackingContext } from "context/Tracking";
import { PrimaryButton } from "components/Button/PrimaryButton";

import { isTimeoutError } from "./api";
import { ProjectContextPanel } from "./ProjectContextPanel";
import { GenerationLoadingView } from "./GenerationLoadingView";
import { useAnalyzeProject } from "./hooks/useAnalyzeProject";

interface StartViewProps {
  projectId: string;
  project: Project;
}

// Stage-1 loading phases (plan §11).
export const ANALYZE_PHASE_KEYS = [
  "risks.ai.loading.analyzing",
  "risks.ai.loading.grouping",
  "risks.ai.loading.evaluating",
  "risks.ai.loading.drafting",
];

// "Thin" project data = empty description AND effectively-empty boundary —
// the AI has almost nothing to work with. Generation stays allowed (plan §5);
// the user just gets an amber warning.
export const isLowContextProject = (project: Project): boolean => {
  const { boundary, projectdata } = project;

  const boundaryIsEmpty =
    !boundary?.weight &&
    !boundary?.width &&
    !boundary?.depth &&
    !boundary?.expected_usage &&
    !boundary?.expected_misuse &&
    !(boundary?.additional_boundaries || []).some(
      (additional) => additional.description || additional.value
    );

  return !projectdata.description?.trim() && boundaryIsEmpty;
};

// START VIEW (no session yet): read-only disclosure of the project data the
// server sends to the AI + a free-text field for additional user context +
// the synchronous analyze kickoff with a multi-phase loader (plan §1).
export const StartView = ({ projectId, project }: StartViewProps) => {
  const { t } = useTranslation();
  const { track } = useContext(TrackingContext);
  const [additionalContext, setAdditionalContext] = useState("");

  const {
    mutate: analyze,
    isLoading,
    isError,
    error,
  } = useAnalyzeProject(projectId);

  const handleGenerate = () => {
    if (isLoading) return;

    track("ai risk: start analysis", {
      mode: "initial",
      has_additional_context: !!additionalContext.trim(),
    });

    // onSuccess (in the hook) writes the returned session into the
    // react-query cache → the page flips from start view to board.
    analyze({
      additional_context: additionalContext.trim(),
      mode: "initial",
    });
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-30">
        <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-sm mx-4">
          <GenerationLoadingView
            phaseKeys={ANALYZE_PHASE_KEYS}
            stepIntervalMs={20000}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      <ProjectContextPanel projectId={projectId} project={project} />

      {isLowContextProject(project) && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">
            {t("risks.ai.start.lowContextWarning")}
          </p>
        </div>
      )}

      <div>
        <label
          htmlFor="ai-risk-additional-context"
          className="block text-sm font-medium text-gray-700"
        >
          {t("risks.ai.start.additionalContext.label")}
        </label>
        <textarea
          id="ai-risk-additional-context"
          rows={4}
          value={additionalContext}
          onChange={(event) => setAdditionalContext(event.target.value)}
          placeholder={t("risks.ai.start.additionalContext.placeholder")}
          className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
        />
      </div>

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">
            {t(
              isTimeoutError(error)
                ? "risks.ai.errors.timeout"
                : "risks.ai.errors.analysisFailed"
            )}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <PrimaryButton testId="ai-risk-analyze" onClick={handleGenerate}>
          {t("risks.ai.start.generate.cta")}
        </PrimaryButton>
      </div>
    </div>
  );
};
